import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Badge, Button, Card, Skeleton } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { Bifa, BaraStare, CampParola, FaraDrept, Grup } from './comune'

type Setari = {
  smtp_host?: string; smtp_port?: number; smtp_username?: string; smtp_password_masked?: string
  smtp_use_tls?: boolean; sender_email?: string; sender_name?: string; reply_to?: string
}

export function SetariEmail() {
  const qc = useQueryClient()
  const [f, setF] = useState<Setari & { parola: string }>({ parola: '' })
  const [salvat, setSalvat] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)
  const [destinatar, setDestinatar] = useState('')
  const [rezultatTest, setRezultatTest] = useState<string | null>(null)

  const setari = useQuery({
    queryKey: ['email-setari'], retry: false,
    queryFn: () => api.get<{ settings: Setari }>('/email/settings'),
  })
  const stare = useQuery({
    queryKey: ['email-stare'], retry: false,
    queryFn: () => api.get<{ available: boolean }>('/email/status'),
  })

  useEffect(() => { if (setari.data?.settings) setF((x) => ({ ...setari.data.settings, parola: x.parola })) }, [setari.data])

  const faraDrept = (setari.error as ApiError | null)?.status === 403

  const salveaza = useMutation({
    mutationFn: () => api.put('/email/settings', {
      smtp_host: f.smtp_host ?? '',
      smtp_port: Number(f.smtp_port ?? 587),
      smtp_username: f.smtp_username ?? '',
      // Parola pleaca doar daca a fost tastata una noua; altfel ramane cea salvata.
      ...(f.parola ? { smtp_password: f.parola } : {}),
      smtp_use_tls: Boolean(f.smtp_use_tls),
      sender_email: f.sender_email ?? '',
      sender_name: f.sender_name ?? '',
      reply_to: f.reply_to ?? '',
    }),
    onSuccess: () => {
      setSalvat(true); setF((x) => ({ ...x, parola: '' }))
      qc.invalidateQueries({ queryKey: ['email-setari'] })
      qc.invalidateQueries({ queryKey: ['email-stare'] })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  const testeaza = useMutation({
    mutationFn: () => api.post<{ success: boolean; message?: string }>('/email/test', { to_email: destinatar }),
    onSuccess: (r) => setRezultatTest(r.success ? `Trimis la ${destinatar}` : (r.message ?? 'Nu s-a putut trimite')),
    onError: (e) => setRezultatTest(e instanceof Error ? e.message : 'Nu s-a putut trimite'),
  })

  if (faraDrept) return <div className="px-8 py-6"><FaraDrept ce="Configurarea emailului" /></div>
  if (setari.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={6} /></Card></div>

  return (
    <div className="px-8 py-6 space-y-5">
      <Card className="p-4 flex items-center gap-3">
        <Badge tone={stare.data?.available ? 'good' : 'bad'}>
          {stare.data?.available ? 'serviciul de email merge' : 'serviciul de email nu e configurat'}
        </Badge>
        <span className="text-[13px] text-muted">
          De aici pleaca voucherele, actele de plecare si facturile trimise din fisa rezervarii.
        </span>
      </Card>

      <Grup titlu="Server SMTP">
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Camp eticheta="Gazda" id="e-host" valoare={f.smtp_host ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, smtp_host: v })) }} />
          <Camp eticheta="Port" id="e-port" tip="number" valoare={String(f.smtp_port ?? 587)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, smtp_port: Number(v) })) }} />
          <Camp eticheta="Utilizator" id="e-user" valoare={f.smtp_username ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, smtp_username: v })) }} />
          <CampParola eticheta="Parola" id="e-pass" valoare={f.parola} masca={f.smtp_password_masked}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, parola: v })) }} />
        </div>
        <div className="mt-3">
          <Bifa eticheta="Conexiune criptata (TLS)" valoare={Boolean(f.smtp_use_tls)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, smtp_use_tls: v })) }} />
        </div>
      </Grup>

      <Grup titlu="Expeditor">
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Camp eticheta="Adresa expeditorului" id="e-sender" tip="email" valoare={f.sender_email ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sender_email: v })) }} />
          <Camp eticheta="Numele expeditorului" id="e-sname" valoare={f.sender_name ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sender_name: v })) }} />
          <Camp eticheta="Raspunsurile merg la" id="e-reply" tip="email" valoare={f.reply_to ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, reply_to: v })) }} latime="plin" />
        </div>
        <div className="mt-4">
          <BaraStare salveaza={() => { setEroare(null); salveaza.mutate() }} seSalveaza={salveaza.isPending}
            salvat={salvat} eroare={eroare} />
        </div>
      </Grup>

      <Grup titlu="Trimite un email de proba">
        <div className="flex flex-wrap items-end gap-3 max-w-2xl">
          <div className="w-72">
            <Camp eticheta="Catre" id="e-test" tip="email" valoare={destinatar} seteaza={setDestinatar} />
          </div>
          <Button onClick={() => { setRezultatTest(null); testeaza.mutate() }}
            disabled={testeaza.isPending || !destinatar}>
            {testeaza.isPending ? 'Se trimite…' : 'Trimite proba'}
          </Button>
          {rezultatTest && <span className="text-[13px] text-muted">{rezultatTest}</span>}
        </div>
      </Grup>
    </div>
  )
}
